import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndustrySelector } from '../../components/ComparisonTool/components/IndustrySelector';
import { LocationPicker } from '../../components/ComparisonTool/components/LocationPicker';
import { ComparisonMatrix } from '../../components/ComparisonTool/components/ComparisonMatrix';
import { EmailSignupForm } from '../../components/ComparisonTool/components/EmailSignupForm';
import { scoreSources, type LeadSourceApiRow } from '../../api/comparisonApi';

interface ComparisonResult {
  source_id: string;
  name: string;
  score: number;
  explanation: string;
  pricing_model: string;
  entry_price?: number;
  cost_per_lead?: number;
  features: Record<string, unknown>;
  requires_known_domain?: boolean;
  url?: string;
}

function mapApiSourceToMatrixRow(
  row: LeadSourceApiRow,
  industryName: string,
  locationName: string,
  startingPoint: string | null,
): ComparisonResult {
  const featureRecord = row.features && typeof row.features === 'object' ? row.features : {};
  const monthlyMinimum = row.monthly_cost_min ?? undefined;

  let explanationText = '';
  const industry = industryName || 'businesses';
  const location = locationName || 'your area';
  const hasColdDiscovery = featureRecord['cold_discovery'];
  const requiresDomain = featureRecord['requires_known_domain'];
  const hasVerification = featureRecord['email_verification'] || featureRecord['verified_emails'];
  const hasDecisionMakers = featureRecord['decision_makers'];
  const isLocalFocus = featureRecord['local_focus'];
  const isEnterpriseOnly = featureRecord['enterprise_focus'] && !hasColdDiscovery;

  if (startingPoint === 'starting_fresh') {
    if (hasColdDiscovery && hasDecisionMakers && hasVerification) {
      explanationText = `Finds ${industry} businesses in ${location} from scratch and delivers verified emails with decision maker names. A complete solution for cold outreach.`;
    } else if (hasColdDiscovery && isLocalFocus && !hasDecisionMakers) {
      explanationText = `${row.name} can find ${industry} businesses in ${location}, but it only surfaces listings. No contact emails or decision maker names, so you'll need a separate tool to actually reach anyone.`;
    } else if (hasColdDiscovery && !hasDecisionMakers) {
      explanationText = `Finds ${industry} businesses by running a live search, but contact enrichment is limited. Good for building a list, less useful for direct outreach.`;
    } else if (requiresDomain) {
      explanationText = `Requires a company domain to look up emails. Since you're starting from scratch without a list, this is a significant limitation.`;
    } else if (isEnterpriseOnly) {
      explanationText = `Built for enterprise sales teams targeting large companies. For local ${industry} businesses, most of its value won't apply.`;
    } else {
      explanationText =
        (row.description && row.description.trim()) ||
        `Ranked using cost, data quality, and fit for ${industry} discovery.`;
    }
  } else if (startingPoint === 'has_list') {
    if (requiresDomain && hasVerification) {
      explanationText = `You already have company domains, and this tool looks up and verifies emails directly from those domains. That's exactly the workflow you need.`;
    } else if (requiresDomain) {
      explanationText = `Domain-based email lookup. A solid fit since you already have company names or websites to work from.`;
    } else if (hasColdDiscovery && hasDecisionMakers && hasVerification) {
      explanationText = `Built for finding new businesses from scratch, but also includes email verification and decision maker lookup. You can use it to enrich the list you already have.`;
    } else if (hasColdDiscovery && isLocalFocus) {
      explanationText = `Built for finding businesses from scratch. Since you already have a list, you're paying for discovery you don't need.`;
    } else if (hasColdDiscovery) {
      explanationText = `Primarily a discovery tool. Since you already have a list, most of what you're paying for doesn't apply here.`;
    } else if (hasVerification) {
      explanationText = `Strong email verification, well suited for validating the contact info on your existing list.`;
    } else {
      explanationText =
        (row.description && row.description.trim()) ||
        `Ranked using cost, data quality, and fit for enriching your list.`;
    }
  } else if (startingPoint === 'enriching') {
    if (hasVerification && hasDecisionMakers) {
      explanationText = `Verifies emails and adds decision maker names. Useful for cleaning your existing contacts and filling in missing information.`;
    } else if (hasVerification && requiresDomain) {
      explanationText = `Domain-based email verification, well suited for cleaning up a list where you have company websites but need to confirm deliverability.`;
    } else if (hasVerification) {
      explanationText = `Strong email verification, well suited for cleaning and validating the contacts you already have.`;
    } else if (requiresDomain) {
      explanationText = `Domain-based lookup useful for enrichment workflows when you have company names but are missing contact details.`;
    } else if (hasColdDiscovery && !hasVerification) {
      explanationText = `Built for finding new businesses from scratch. Since you're enriching existing contacts, the core feature doesn't apply and there's no dedicated verification.`;
    } else {
      explanationText =
        (row.description && row.description.trim()) ||
        `Ranked using cost, data quality, and verification features.`;
    }
  } else {
    if (hasColdDiscovery && hasDecisionMakers && hasVerification) {
      explanationText = `Finds ${industry} businesses in ${location}, enriches them with decision maker emails, and verifies deliverability.`;
    } else if (hasColdDiscovery && isLocalFocus) {
      explanationText = `Finds local businesses in ${location}. Good for building a raw list, but limited on contact data.`;
    } else if (hasColdDiscovery) {
      explanationText = `Finds ${industry} businesses by searching live data.`;
    } else if (requiresDomain) {
      explanationText = `Looks up emails for companies you already know. Needs a domain or website to start.`;
    } else if (hasVerification) {
      explanationText = `Strong email verification and validation.`;
    } else {
      explanationText =
        (row.description && row.description.trim()) ||
        'Ranked using cost, data quality, feature breadth, and fit for your industry.';
    }
  }

  return {
    source_id: String(row.id),
    name: row.name,
    score: Math.round(row.final_score * 1000) / 10,
    explanation: explanationText,
    pricing_model: row.pricing_model,
    entry_price: monthlyMinimum,
    cost_per_lead: row.cost_per_lead ?? undefined,
    features: featureRecord,
    requires_known_domain: Boolean(featureRecord['requires_known_domain']),
    url: row.url ?? undefined,
  };
}

// ── Types ─────────────────────────────────────────────────────────────────────
type Step = 'industry' | 'location' | 'starting_point' | 'preferences' | 'results';
type TargetMarket = 'local_smb' | 'mixed' | 'national_enterprise';
type EmailPriority = 'nice' | 'important' | 'critical';
type StartingPoint = 'starting_fresh' | 'has_list' | 'enriching';

interface RadioOption<T extends string> {
  value: T;
  label: string;
  subtitle: string;
  icon: string;
}

const TARGET_MARKET_OPTIONS: RadioOption<TargetMarket>[] = [
  { value: 'local_smb', label: 'Local businesses', subtitle: 'Shops, services, trades in a city or region', icon: '📍' },
  { value: 'mixed', label: 'Mix of local & national', subtitle: 'Some small accounts, some bigger ones', icon: '🗺️' },
  { value: 'national_enterprise', label: 'National or enterprise', subtitle: 'Larger companies across many markets', icon: '🏢' },
];

const EMAIL_PRIORITY_OPTIONS: RadioOption<EmailPriority>[] = [
  { value: 'nice', label: 'Nice to have', subtitle: 'I can work with partial contact info', icon: '💬' },
  { value: 'important', label: 'Important', subtitle: 'I need reliable emails most of the time', icon: '📧' },
  { value: 'critical', label: 'Critical (bounces hurt me)', subtitle: 'I run cold email at volume', icon: '🎯' },
];

const STARTING_POINT_OPTIONS: RadioOption<StartingPoint>[] = [
  { value: 'starting_fresh', label: 'Starting from scratch', subtitle: "I need to find businesses. I don't have a list yet.", icon: '🔍' },
  { value: 'has_list', label: 'I have a business list', subtitle: 'I have companies or domains, I need contact details.', icon: '📋' },
  { value: 'enriching', label: 'I have contacts', subtitle: 'I have emails or names and want to verify or enrich them.', icon: '✉️' },
];

const STEP_ORDER: Step[] = ['industry', 'location', 'starting_point', 'preferences', 'results'];
const STEP_LABELS: Record<Step, string> = {
  industry: 'Industry',
  location: 'Location',
  starting_point: 'Starting Point',
  preferences: 'Your Needs',
  results: 'Results',
};

// ── Radio Card ────────────────────────────────────────────────────────────────
function RadioCard<T extends string>({
  options,
  value,
  onChange,
}: {
  options: RadioOption<T>[];
  value: T | null;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 560 }}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
              padding: '18px 22px',
              borderRadius: 14,
              border: `2px solid ${active ? '#8A3033' : '#e5e7eb'}`,
              background: active ? '#FDF0F0' : '#fff',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!active) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(138,48,51,0.35)';
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(138,48,51,0.04)';
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                (e.currentTarget as HTMLButtonElement).style.borderColor = '#e5e7eb';
                (e.currentTarget as HTMLButtonElement).style.background = '#fff';
              }
            }}
          >
            <div
              style={{
                width: 40, height: 40,
                borderRadius: 10,
                background: active ? '#8A3033' : 'rgba(45,49,66,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
                transition: 'background 0.15s',
              }}
            >
              {opt.icon}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: 15, color: active ? '#8A3033' : '#2D3142', marginBottom: 3 }}>
                {opt.label}
              </p>
              <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>{opt.subtitle}</p>
            </div>
            {/* Radio dot */}
            <div
              style={{
                width: 20, height: 20,
                borderRadius: '50%',
                border: `2px solid ${active ? '#8A3033' : '#d1d5db'}`,
                background: active ? '#8A3033' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 2,
                transition: 'all 0.15s',
              }}
            >
              {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Stepper ───────────────────────────────────────────────────────────────────
function Stepper({
  currentStep,
  industryName,
  locationName,
  startingPoint,
}: {
  currentStep: Step;
  industryName: string;
  locationName: string;
  startingPoint: StartingPoint | null;
}) {
  const currentIdx = STEP_ORDER.indexOf(currentStep);
  const startingPtLabel = startingPoint
    ? ({ starting_fresh: 'From scratch', has_list: 'Has a list', enriching: 'Has contacts' } as const)[startingPoint]
    : null;
  const chips = [industryName, locationName, startingPtLabel].filter(Boolean) as string[];

  return (
    <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0 40px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '20px 0' }}>
        {/* Step indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: chips.length ? 14 : 0 }}>
          {STEP_ORDER.map((s, i) => {
            const done = i < currentIdx;
            const active = s === currentStep;
            return (
              <React.Fragment key={s}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div
                    style={{
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700,
                      background: done ? '#8A3033' : active ? '#FDF0F0' : 'transparent',
                      color: done ? '#fff' : active ? '#8A3033' : '#9ca3af',
                      border: `2px solid ${done ? '#8A3033' : active ? '#8A3033' : '#e5e7eb'}`,
                      transition: 'all 0.3s',
                    }}
                  >
                    {done ? '✓' : i + 1}
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      color: active ? '#8A3033' : done ? '#2D3142' : '#9ca3af',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {STEP_LABELS[s]}
                  </span>
                </div>
                {i < STEP_ORDER.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: 1.5,
                      background: done ? '#8A3033' : '#e5e7eb',
                      margin: '0 10px',
                      transition: 'background 0.4s',
                      minWidth: 16,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Context chips */}
        {chips.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {chips.map((chip) => (
              <span
                key={chip}
                style={{
                  padding: '3px 10px',
                  borderRadius: 99,
                  background: '#FDF0F0',
                  color: '#8A3033',
                  fontSize: 12,
                  fontWeight: 600,
                  border: '1px solid rgba(138,48,51,0.15)',
                }}
              >
                {chip}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── WebLeads CTA block (wraps EmailSignupForm) ────────────────────────────────
function WebLeadsCTABlock({
  industryName,
  locationName,
  startingPoint,
  queryId,
  onSuccess,
}: {
  industryName: string;
  locationName: string;
  startingPoint: StartingPoint | null;
  queryId: number | null;
  onSuccess: (email: string) => void;
}) {
  const headline =
    industryName && locationName
      ? `The best lead gen tool for ${industryName} in ${locationName}. Try it free.`
      : industryName
      ? `Top-ranked for ${industryName}. Try WebLeads free.`
      : 'WebLeads ranked in your top 3. Try it free, no card needed.';

  const sub =
    startingPoint === 'starting_fresh'
      ? 'Find businesses from scratch · Real-time search · Starts at $24/mo'
      : startingPoint === 'has_list'
      ? 'Enrich your list with contacts · Decision makers · Starts at $24/mo'
      : startingPoint === 'enriching'
      ? 'Verify & enrich your contacts · Email verified · Starts at $24/mo'
      : 'Real-time search · Decision makers · Verified emails · $24/mo';

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #2D3142 0%, #3d4560 100%)',
        borderRadius: 20,
        padding: '28px 32px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Crimson blob */}
      <div
        style={{
          position: 'absolute', top: -40, right: -40,
          width: 200, height: 200, borderRadius: '50%',
          background: '#8A3033', opacity: 0.2, filter: 'blur(50px)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'center', gap: 28,
          flexWrap: 'wrap', justifyContent: 'space-between',
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 99, padding: '3px 10px', marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 13 }}>🚀</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Made by WebLeads
            </span>
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 5, lineHeight: 1.3, letterSpacing: '-0.3px' }}>
            {headline}
          </h3>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>{sub}</p>
        </div>

        <div style={{ flex: '1 1 300px', minWidth: 260 }}>
          <EmailSignupForm
            onSuccess={onSuccess}
            industry={industryName}
            location={locationName}
            startingPoint={startingPoint}
            queryId={queryId}
          />
        </div>
      </div>
    </div>
  );
}

// ── Continue Button ───────────────────────────────────────────────────────────
function ContinueButton({ onClick, disabled, label = 'Continue' }: { onClick: () => void; disabled: boolean; label?: string }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        marginTop: 32,
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '14px 32px', borderRadius: 10,
        background: disabled ? 'rgba(138,48,51,0.25)' : '#8A3033',
        color: '#fff', border: 'none',
        fontWeight: 700, fontSize: 15,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
      }}
    >
      {label} →
    </button>
  );
}

// ── Main Form ─────────────────────────────────────────────────────────────────
export const ComparisonForm: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('industry');
  const [industryId, setIndustryId] = useState<string | null>(null);
  const [industryName, setIndustryName] = useState<string>('');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string>('');
  const [startingPoint, setStartingPoint] = useState<StartingPoint | null>(null);
  const [targetMarket, setTargetMarket] = useState<TargetMarket | null>(null);
  const [emailPriority, setEmailPriority] = useState<EmailPriority | null>(null);
  const [results, setResults] = useState<ComparisonResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryId, setQueryId] = useState<number | null>(null);

  const handleIndustryChange = (id: string, name: string) => {
    setIndustryId(id);
    setIndustryName(name);
    setStep('location');
  };

  const handleLocationChange = (id: string, name: string) => {
    setLocationId(id);
    setLocationName(name);
    setStep('starting_point');
  };

  const handleGetResults = async () => {
    if (!industryId || !locationId || !startingPoint) {
      setError('Please complete all steps');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await scoreSources(
        industryName, locationName, locationId,
        targetMarket, emailPriority, startingPoint,
      );
      const rawSources: LeadSourceApiRow[] = Array.isArray(data.sources) ? data.sources : [];
      setResults(rawSources.map((row) => mapApiSourceToMatrixRow(row, industryName, locationName, startingPoint)));
      setQueryId(data.query_id ?? null);
      setStep('results');

      if (window.gtag) {
        window.gtag('event', 'comparison_completed', {
          industry: industryName,
          location: locationName,
          starting_point: startingPoint,
          target_market: targetMarket,
          email_priority: emailPriority,
        });
      }
    } catch (scoreError) {
      setError(scoreError instanceof Error ? scoreError.message : 'Failed to get results');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignupSuccess = (email: string) => {
    if (window.gtag) {
      window.gtag('event', 'email_signup', {
        email_domain: email.split('@')[1],
        starting_point: startingPoint,
        industry: industryName,
      });
    }
  };

  const canSubmitPreferences = startingPoint !== null && targetMarket !== null && emailPriority !== null;

  return (
    <div style={{ minHeight: '100vh', background: '#F9F5F5' }}>
      <Stepper
        currentStep={step}
        industryName={industryName}
        locationName={locationName}
        startingPoint={startingPoint}
      />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 40px' }}>

        {/* Back to tool landing */}
        {step !== 'results' && (
          <button
            onClick={() => navigate('/comparison-tool')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none',
              color: '#8A3033', fontWeight: 500, cursor: 'pointer',
              fontSize: 14, padding: 0, marginBottom: 32,
            }}
          >
            ← Back to overview
          </button>
        )}

        {/* ── Step: Industry ── */}
        {step === 'industry' && (
          <div>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: '#2D3142', letterSpacing: '-0.8px', marginBottom: 8 }}>
              What industry are you in?
            </h2>
            <p style={{ fontSize: 15, color: '#6b7280', marginBottom: 28 }}>
              We'll weight the tools that work best for your vertical.
            </p>
            <IndustrySelector value={industryId} onChange={handleIndustryChange} />
          </div>
        )}

        {/* ── Step: Location ── */}
        {step === 'location' && (
          <div>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: '#2D3142', letterSpacing: '-0.8px', marginBottom: 8 }}>
              Where do you operate?
            </h2>
            <p style={{ fontSize: 15, color: '#6b7280', marginBottom: 28 }}>
              Some tools have far better coverage in certain regions.
            </p>
            <LocationPicker value={locationId} onChange={handleLocationChange} />
            <button
              onClick={() => setStep('industry')}
              style={{ marginTop: 28, background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14, fontWeight: 500, padding: 0 }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* ── Step: Starting Point ── */}
        {step === 'starting_point' && (
          <div>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: '#2D3142', letterSpacing: '-0.8px', marginBottom: 8 }}>
              What's your starting point?
            </h2>
            <p style={{ fontSize: 15, color: '#6b7280', marginBottom: 32 }}>
              This determines which tool types will serve you best.
            </p>
            <RadioCard
              options={STARTING_POINT_OPTIONS}
              value={startingPoint}
              onChange={setStartingPoint}
            />
            {error && <p style={{ fontSize: 13, color: '#ef4444', marginTop: 12 }}>{error}</p>}
            <ContinueButton
              onClick={() => setStep('preferences')}
              disabled={startingPoint === null}
            />
            <button
              onClick={() => setStep('location')}
              style={{ display: 'block', marginTop: 16, background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14, fontWeight: 500, padding: 0 }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* ── Step: Preferences ── */}
        {step === 'preferences' && (
          <div>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: '#2D3142', letterSpacing: '-0.8px', marginBottom: 8 }}>
              Two quick questions
            </h2>
            <p style={{ fontSize: 15, color: '#6b7280', marginBottom: 36 }}>
              This fine-tunes your match score significantly.
            </p>

            <div style={{ marginBottom: 36 }}>
              <p style={{ fontWeight: 700, fontSize: 16, color: '#2D3142', marginBottom: 16 }}>
                Who are your target customers?
              </p>
              <RadioCard options={TARGET_MARKET_OPTIONS} value={targetMarket} onChange={setTargetMarket} />
            </div>

            <div>
              <p style={{ fontWeight: 700, fontSize: 16, color: '#2D3142', marginBottom: 16 }}>
                How critical are verified emails?
              </p>
              <RadioCard options={EMAIL_PRIORITY_OPTIONS} value={emailPriority} onChange={setEmailPriority} />
            </div>

            {error && <p style={{ fontSize: 13, color: '#ef4444', marginTop: 12 }}>{error}</p>}

            <ContinueButton
              onClick={handleGetResults}
              disabled={loading || !canSubmitPreferences}
              label={loading ? 'Getting Results...' : 'See My Recommendations'}
            />
            <button
              onClick={() => setStep('starting_point')}
              style={{ display: 'block', marginTop: 16, background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14, fontWeight: 500, padding: 0 }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* ── Step: Results ── */}
        {step === 'results' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
              <div>
                <h2 style={{ fontSize: 28, fontWeight: 800, color: '#2D3142', letterSpacing: '-0.6px', marginBottom: 8 }}>
                  Your Top Picks
                </h2>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    industryName,
                    locationName,
                    startingPoint && ({ starting_fresh: 'Starting from scratch', has_list: 'Has a list', enriching: 'Has contacts' } as const)[startingPoint],
                  ].filter(Boolean).map((tag) => (
                    <span
                      key={tag as string}
                      style={{
                        padding: '3px 10px', borderRadius: 99,
                        background: '#2D3142', color: '#fff',
                        fontSize: 12, fontWeight: 600,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Results with inline CTA after top 3 */}
            <ComparisonMatrix
              results={results}
              onSelectTool={(tool) => {
                if (tool.name === 'WebLeads') {
                  window.location.href = 'https://app.webleads.site/register';
                } else if (tool.url) {
                  window.open(tool.url, '_blank');
                }
              }}
              loading={false}
            />

            {/* WebLeads CTA block */}
            <div style={{ marginTop: 20, marginBottom: 8 }}>
              <WebLeadsCTABlock
                industryName={industryName}
                locationName={locationName}
                startingPoint={startingPoint}
                queryId={queryId}
                onSuccess={handleEmailSignupSuccess}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonForm;
